import React, {useState, useRef, useEffect} from 'react';

import {
  Add as AddIcon,
  MoreHoriz,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Backdrop,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  MenuItem,
  SvgIcon,
  Typography,
} from '@mui/material';
import {IconAlertCircleFillYes, IconCheckCircle, IconCheckCircleFill} from '@zoom/sun-ui-icon';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

import {ConnectionErrorResolutionDialog} from './ConnectionErrorDialog';
import {
  AccountRow,
  ActionsArea,
  AddAccountTips,
  RowEmailAdress,
  RowNameAndService,
  SmallIcon,
  StatusText,
  TextBtn,
  TypographyDesc,
  TypographySub,
  TypographyTitle,
} from './Style';

import AvailabilityPreference from '../AvailabilityPreference';
import SyncPreference from '../SyncPreference';

import {ReactComponent as AlertIcon} from 'assets/svg/alert-filled.svg';
import {ReactComponent as GoogleIcon} from 'assets/svg/google-icon.svg';
import {ReactComponent as ICloudIcon} from 'assets/svg/icloud.svg';
import {ReactComponent as MSIcon} from 'assets/svg/ms-icon.svg';
import {ReactComponent as ZimbraIcon} from 'assets/svg/zimbra-icon.svg';
import {ReactComponent as ZoomIcon} from 'assets/svg/zoom-icon.svg';
import ErrorAlert from 'Components/Error/ErrorAlert';
import ConnectAccountModal from 'Components/Integration/ConnectAccountModal';
import {ZmPrimaryBtn, ZmTertiaryBtn} from 'Components/ZmButtons';
import {ZmMenu, ZmDialog} from 'Components/ZmDialogs';
import {ZmSwitch} from 'Components/ZmInputs';
import ZmSpinner from 'Components/ZmSpinner';
import {ZmTooltip} from 'Components/ZmTooltip/ZmTooltip';
import {
  disconnectAccount,
  getAccountKey,
  isAccountMatch,
  listAccounts,
  selectThirdPartyAccountReachLimit,
  // switchAccount,
  toggleAvailabilityPreferences,
} from 'Store/IntegrationStore';
import {selectBindAccount} from 'Store/IntegrationStore.js';
import {isCciToken, parseName} from 'Utils';
import {GET_CONN_ERROR, PROVIDER_TYPE} from 'Utils/consts';
import {inZoomClient, useConnect3rdParty} from 'Utils/integration';
import {openUrlInBrowser, WEB_SCHEDULER_URL} from 'Utils/ZoomClientUtils';
import zoomPalette from 'zoomPalette.js';

const {APPLE, GOOGLE_CALENDAR, OUTLOOK_CALENDAR, ZIMBRA, ZOOM} = PROVIDER_TYPE;

export const iconMap = {
  [GOOGLE_CALENDAR]: GoogleIcon,
  [ZOOM]: ZoomIcon,
  [OUTLOOK_CALENDAR]: MSIcon,
  [APPLE]: ICloudIcon,
  [ZIMBRA]: ZimbraIcon,
};
export const serviceNameMap = {
  [GOOGLE_CALENDAR]: 'Google',
  [ZOOM]: 'Zoom',
  [OUTLOOK_CALENDAR]: 'Office 365',
  [APPLE]: 'iCloud',
  [ZIMBRA]: 'Zimbra',
};
const disableStyle = {
  color: '#6E7680',
  opacity: 0.8,
};
const dropDownBtnOptions = [
  {option: 'integration.reAuthorize', visible: [GOOGLE_CALENDAR, OUTLOOK_CALENDAR]}, // zmail has no re-auth operation
  {option: 'integration.disconnect', visible: [APPLE, GOOGLE_CALENDAR, OUTLOOK_CALENDAR, ZIMBRA, ZOOM]},
];
/**
 * Account Card Component
 * @param {Object} props
 * @param {Account} props.data
 * @param {function} props.onClickMore
 * @param {function} props.onSwitch
 * @return {ReactElement}
 */
function Row({data, onClickMore, onReAuthorize, onSwitch}) {
  const {t} = useTranslation();
  const moreBtnRef = useRef(null);
  const name = parseName(data.email);

  const pushCalendarEnabledAndValid = accountHasValidPushCalendar(data);
  const pushCalendarMatchesPullCalendar = accountPushCalendarMatchesPullCalendar(data);
  const accountInvalid = accountAccessTokenInvalid(data);

  return (
    <AccountRow
      aria-label={`edit account settings for ${data.email}`}
      role="button"
      onClick={onSwitch}
      // fix: the edit button won't disappear even when mouse move out of the area after clicking on/off toggle
      onMouseLeave={() => moreBtnRef.current?.blur()}
    >
      <div style={{width: '16px', height: '16px', marginRight: '16px'}}>
        {pushCalendarEnabledAndValid ?
          <IconCheckCircleFill size={16} color={zoomPalette.green['60']} /> :
          <IconCheckCircle className="makePrimaryIcon" size={16} />
        }
      </div>
      <SvgIcon
        sx={{width: '48px', height: '48px', marginRight: '16px', paddingTop: '2px'}}
        component={iconMap[data.service]}
        inheritViewBox
      ></SvgIcon>
      <div>
        <RowNameAndService>
          {name[0] + ' ' + (name?.[1] || '')} ({serviceNameMap[data.service]})
        </RowNameAndService>
        <RowEmailAdress>{data.email}</RowEmailAdress>
        {data.pushEnabled &&
          <TypographySub sx={{display: 'inline-flex', gap: '4px'}}>
            <span>
              {t('integration.addEventsToCalendar')}
            </span>
            {
              pushCalendarEnabledAndValid ?
                <>
                  {data.calendars.find((cal) => cal.id === data.pushCalendarId).name}
                  {!pushCalendarMatchesPullCalendar && <Box
                    component="span"
                    sx={{display: 'inline-flex', alignItems: 'center', marginLeft: '4px', gap: '4px'}}
                  >
                    <IconAlertCircleFillYes />
                    <FormHelperText component="span" sx={{color: zoomPalette.gold[60], display: 'inline', margin: 0}}>
                      {t('integration.notCheckingConflictsPrimaryAccount')}
                    </FormHelperText>
                  </Box>}
                </> :
                <Box
                  component="span"
                  sx={{display: 'inline-flex', alignItems: 'center', marginLeft: '4px', gap: '4px'}}
                >
                  <IconAlertCircleFillYes />
                  <FormHelperText component="span" sx={{color: zoomPalette.gold[60], display: 'inline', margin: 0}}>
                    {t('integration.missingPushCalendarId')}
                  </FormHelperText>
                </Box>
            }
          </TypographySub>
        }
      </div>
      <ActionsArea>
        {
          !accountInvalid && <ZmTertiaryBtn size="xsmall" className="hoverAction">
            {(!pushCalendarEnabledAndValid) ?
              t('integration.switchPrimaryAccount') :
              t('common.edit')
            }
          </ZmTertiaryBtn>
        }

        {data.service !== ZOOM && !!data.blocked && accountAccessTokenInvalid(data) &&
          <>
            <SmallIcon component={AlertIcon} inheritViewBox></SmallIcon>
            <StatusText>{t('integration.accountErrorState')}</StatusText>
            <TextBtn onClick={(e) => {
              e.stopPropagation();
              if (inZoomClient) {
                openUrlInBrowser(`${WEB_SCHEDULER_URL}?action=settings-account`);
              } else {
                onReAuthorize(data.service);
              }
            }}>{t('integration.reAuthorize')}</TextBtn>
          </>
        }
        <ZmTooltip
          title={t('integration.accountToolTip')}
        >
          <ZmTertiaryBtn
            size="xsmall"
            sx={{
              minWidth: '24px',
              width: '24px',
              height: '24px',
              marginLeft: '20px',
            }}
            aria-haspopup="menu"
            onClick={(e) => {
              e.stopPropagation();
              onClickMore(moreBtnRef);
            }}
            ref={moreBtnRef}
          >
            <SmallIcon component={MoreHoriz} inheritViewBox></SmallIcon>
          </ZmTertiaryBtn>
        </ZmTooltip>
      </ActionsArea>
    </AccountRow>
  );
}

/**
 * Account Card Component
 * @param {Object} props
 * @param {Account} props.data
 * @param {boolean} props.disabled
 * @param {function} props.onClick
 * @param {function} props.onToggle
 * @return {ReactElement}
 */
function AvailabilitySettingsItem({data, disabled, onClick, onToggle}) {
  const {t} = useTranslation();
  const editBtnRef = useRef(null);

  const calendars = data?.calendars || [];
  const checkingCalendars = calendars.filter((cal) => data.pullCalendarIds.includes(cal.id));
  const pushCalendarEnabledAndValid = accountHasValidPushCalendar(data);
  const pushCalendarMatchesPullCalendar = accountPushCalendarMatchesPullCalendar(data);

  // if account is blocked, but pullEnabled is true, we should treat check conflict as enabled.
  // because blocked account can not get the calendars list,
  // so we can not check if the pullCalendarIds are in the calendars list.
  const isBlockedAndCheckConflictEnabled = data.blocked && data.pullEnabled;

  return (
    <AccountRow
      aria-label={`edit conflict settings for ${data.email}`}
      role="button"
      aria-disabled={disabled}
      disabled={disabled}
      onClick={() => {
        if (!disabled) {
          onClick(editBtnRef);
        }
      }}
    >
      <Box sx={{
        alignSelf: 'start',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}>
        <SvgIcon
          sx={{
            width: '48px', height: '48px',
            marginRight: '16px', alignSelf: 'start',
            paddingTop: '2px',
            opacity: checkingCalendars.length > 0 ? 1 : .5,
          }}
          component={iconMap[data.service]}
          inheritViewBox
        />
        <Box>
          <RowNameAndService sx={{
            color: checkingCalendars.length > 0 ? zoomPalette.gray[90] : zoomPalette.gray[60],
          }}>
            {data.email}</RowNameAndService>
          {(checkingCalendars.length > 0) ?
            <>
              <TypographySub sx={{color: zoomPalette.gray[90], fontWeight: 400, marginTop: '6px'}}>
                {t('integration.checkingConflicts')}
              </TypographySub>
              <TypographySub sx={{fontWeight: 400}}>
                {checkingCalendars.map((cal) => {
                  if (pushCalendarEnabledAndValid &&
                    pushCalendarMatchesPullCalendar &&
                    cal?.id === data?.pushCalendarId
                  ) {
                    return `${cal.name} (${t('integration.primaryAccount')})`;
                  }
                  return cal.name;
                }).join(', ')}
              </TypographySub>
            </> :
            <TypographySub>
              {/* Blocked account can not get calendar list, so we don't display anything here. */}
              {isBlockedAndCheckConflictEnabled? '': t('integration.notCheckingConflicts')}
            </TypographySub>
          }
          {pushCalendarEnabledAndValid && !pushCalendarMatchesPullCalendar && <Box
            component="span"
            sx={{display: 'inline-flex', alignItems: 'center', marginLeft: '4px', gap: '4px'}}
          >
            <IconAlertCircleFillYes />
            <FormHelperText component="span" sx={{color: zoomPalette.gold[60], display: 'inline', margin: 0}}>
              {t('integration.notCheckingConflictsPrimaryAccount')}
            </FormHelperText>
          </Box>}
        </Box>
      </Box>
      <ActionsArea style={{alignSelf: 'start', paddingTop: '6px'}}>
        <ZmTertiaryBtn
          ref={editBtnRef}
          className="hoverAction"
          size="small"
          sx={{
            marginRight: '14px',
          }}
          disabled={disabled}
        >
          {t('common.edit')}
        </ZmTertiaryBtn>

        <ZmSwitch
          aria-label={`toggle conflict checking for ${data.email}`}
          size="small"
          checked={
            checkingCalendars.length > 0 ||
            (pushCalendarEnabledAndValid && pushCalendarMatchesPullCalendar) ||
            isBlockedAndCheckConflictEnabled
          }
          disabled={disabled}
          onChange={(e) => {
            onToggle();
          }}
          onClick={(e) => {
            e.stopPropagation();
            // fix: the edit button won't disappear even when mouse move out of the area after clicking on/off toggle
            e.target.blur();
          }}
        />
      </ActionsArea>
    </AccountRow>
  );
}

/**
 * Returns true if account is:
 * - pushEnabled
 * - pushCalendarId valid (in the calendarList)
 * - pushCalendarId's calendar has writeAccess: true
 * @param {Account} acc
 * @return {boolean}
 */
const accountHasValidPushCalendar = (acc) =>
  acc.pushEnabled &&
  (acc?.calendars || []).some(
    (cal) => cal.id === acc.pushCalendarId && cal.writeAccess
  );

/**
 * Returns true if account's pushCalendarId is in pullCalendarIds list
 * @param {Account} acc
 * @return {boolean}
 */
const accountPushCalendarMatchesPullCalendar = (acc) =>
  acc?.pullCalendarIds?.includes(acc?.pushCalendarId);

/**
 * Returns true if account's access-token is invalid
 * @param {Account} acc
 * @return {boolean}
 */
export const accountAccessTokenInvalid = (acc) => acc.blocked && acc.error === GET_CONN_ERROR.INVALID_AUTH;

/**
 * Account Settings Component
 * @return {ReactElement}
 */
export default function AccountSettings() {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const loadingState = useSelector((state) => state.integrationState.loadingState);
  const errorState = useSelector((state) => state.integrationState.errorState);
  const thirdPartyAccountReachLimit = useSelector(selectThirdPartyAccountReachLimit);
  const [showMenu, setShowMenu] = useState(false);
  const [recentlyAddedAccount, setRecentlyAddedAccount] = useState({});
  const {goToAuth, isFetchingOAuthURL} = useConnect3rdParty();
  const [activeItemOnMenu, setActiveItemOnMenu] = useState();
  const [showConnectAccountModal, setShowConnectAccountModal] = useState(false);
  /** @type {Account[]} */
  const accountList = useSelector((s) => s.integrationState.accounts.value);
  const accountCalendars = useSelector((s) => s.integrationState.accountCalendars);

  // Join the bind account data with the calendarsList data
  /** @type {Account[]} */
  const list = accountList.map((account) => ({
    ...account,
    calendars: accountCalendars.mapping[getAccountKey(account)],
  }));

  const hasValidPushCalendar = list.some(accountHasValidPushCalendar);

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showMakeAccountDefaultConfirm, setShowMakeAccountDefaultConfirm] = useState(false);
  const [showAvailabilityPreference, setShowAvailabilityPreference] = useState(false);
  const activeMoreBtnRef = useRef(null);
  const isCciAccount = isCciToken();
  const bindAccount = useSelector(selectBindAccount);
  const [isCancellationsDialogOpen, setIsCancellationsDialogOpen] = useState(false);

  useEffect(() => {
    dispatch(listAccounts());
  }, [dispatch]);

  return <>
    <TypographyTitle>{t('integration.linkedAccounts')}</TypographyTitle>
    <TypographyDesc sx={{marginBottom: '12px'}}>{t('integration.linkedAccountsDesc')}</TypographyDesc>
    {(accountCalendars.hasLoaded && !hasValidPushCalendar) &&
      <Alert
        severity="error"
        variant="standard"
        sx={{
          borderRadius: 3,
          marginY: 1,
        }}
      >
        <AlertTitle>{t('integration.missingPushCalendarTitle')}</AlertTitle>
        {t('integration.missingPushCalendarDesc')}
      </Alert>
    }
    <Box sx={{position: 'relative', minHeight: '140px'}}>
      {!errorState.isError && (loadingState.listAccountState === 'pending' ||
        !accountCalendars.hasLoaded || loadingState.isLoading || isFetchingOAuthURL) &&
        <Backdrop
          open
          sx={{
            position: 'absolute',
            zIndex: 9999,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
          }}
        >
          <ZmSpinner size="40" />
        </Backdrop>
      }
      {errorState.isError && <ErrorAlert reason={t(errorState.reason)} />}
      {loadingState.listAccountState !== 'pending' && !errorState.isError &&
      <>
        <ZmMenu
          open={showMenu}
          anchorEl={activeMoreBtnRef.current}
          onClose={() => setShowMenu(false)}
          sx={{
            '& .MuiPaper-root .MuiMenuItem-root:hover': {
              color: '#fff !important',
            },
          }}
        >
          {dropDownBtnOptions.map(({option, visible}, index) => {
            if (visible.includes(activeItemOnMenu?.service)) {
              return (
                [
                  <MenuItem
                    key={option}
                    // syncing cancellation is only allowed for bind accounts
                    disabled={option === 'integration.editCancellationPreferences' &&
                      !isAccountMatch(bindAccount, activeItemOnMenu)}
                    onClick={() => {
                      switch (option) {
                        case 'integration.disconnect':
                          setShowDisconnectConfirm(true);
                          break;
                        case 'integration.reAuthorize':
                          if (inZoomClient) {
                            openUrlInBrowser(`${WEB_SCHEDULER_URL}?action=settings-account`);
                          } else {
                            goToAuth(activeItemOnMenu?.service);
                          }
                          break;
                        case 'integration.editCancellationPreferences':
                          setIsCancellationsDialogOpen(true);
                          break;
                        case 'integration.editAvailabilityPreference':
                          setShowAvailabilityPreference(true);
                          break;
                        default:
                          break;
                      }
                    }}
                    style={{color: option === 'integration.disconnect' ? '#E8173D' : '#131619', fontWeight: 450}}
                  >
                    <span>{t(option)}</span>
                  </MenuItem>,
                  (option === 'integration.editAvailabilityPreference' || option === 'integration.reAuthorize') &&
                    <Divider
                      key={`divider-${option}`}
                      style={{margin: 0}}
                      aria-hidden={true}
                    />,
                ]
              );
            }
            return null;
          })}
        </ZmMenu>
        {list
          .filter((item) => (
            isCciAccount ? item.service === PROVIDER_TYPE.ZOOM : true))
          .map((item) => (
            <React.Fragment key={getAccountKey(item)}>
              <Row
                key={getAccountKey(item)}
                data={item}
                onClickMore={(el) => {
                  activeMoreBtnRef.current = el.current;
                  setActiveItemOnMenu(item);
                  setShowMenu(true);
                }}
                onSwitch={(e) => {
                  if (accountAccessTokenInvalid(item)) {
                    return;
                  }
                  setActiveItemOnMenu(item);
                  setIsCancellationsDialogOpen(true);
                }}
                onReAuthorize={goToAuth}
              />
              <Divider sx={{margin: '12px 0px'}} />
            </React.Fragment>
          ))
        }
        {!isCciAccount && <div style={{marginTop: '18px', display: 'flex', alignItems: 'center'}}>
          <Button
            style={{
              color: '#131619',
              borderColor: zoomPalette.gray[30],
              width: '14rem',
              height: '32px',
              fontWeight: 500,
              borderRadius: '8px',
              ...(thirdPartyAccountReachLimit ? disableStyle : {}),
            }}
            disabled={thirdPartyAccountReachLimit}
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={(e) => {
              if (inZoomClient) {
                openUrlInBrowser(`${WEB_SCHEDULER_URL}?action=settings-account`);
              } else {
                setShowConnectAccountModal(true);
              }
            }}
          >
            {t('integration.addAnotherAccount')}
          </Button>
          {
            thirdPartyAccountReachLimit && <AddAccountTips>
              {t('integration.thirdPartyAccountLimit')}
            </AddAccountTips>
          }
        </div>}

        <ConnectionErrorResolutionDialog
          accountList={list}
          onDisconnect={async (account) => {
            const {meta} = await dispatch(
              disconnectAccount(account)
            );
            if (meta.requestStatus === 'fulfilled') {
              setShowDisconnectConfirm(false);
            }
          }}
          onReAuthorize={goToAuth}
        />

        {/* Push / Pull config settings */}
        <Box sx={{marginTop: 2, marginBottom: 4, position: 'relative'}}>
          <Backdrop
            open={!hasValidPushCalendar}
            sx={{
              position: 'absolute',
              zIndex: 999,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
            }}
          >
            <Typography>
              {t('integration.conflictSettingsDisabled')}
            </Typography>
          </Backdrop>
          <Box sx={{marginBottom: 2}}>
            <TypographyTitle>
              {t('integration.conflictSectionTitle')}
            </TypographyTitle>
            <TypographyDesc sx={{marginBottom: '12px'}}>
              {t('integration.conflictSectionDesc')}
            </TypographyDesc>
            {list
              .filter((account) => isCciAccount ? account.service === PROVIDER_TYPE.ZOOM : true)
              .map((item) => (
                <React.Fragment key={getAccountKey(item)}>
                  <AvailabilitySettingsItem
                    key={getAccountKey(item)}
                    data={item}
                    disabled={!hasValidPushCalendar}
                    onClick={(el) => {
                      activeMoreBtnRef.current = el.current;
                      setActiveItemOnMenu(item);
                      setShowAvailabilityPreference(true);
                    }}
                    onToggle={() => {
                      if (item.pullEnabled) {
                        dispatch(toggleAvailabilityPreferences({
                          accountEmail: item.email,
                          accountService: item.service,
                        }));
                      } else {
                        dispatch(toggleAvailabilityPreferences({
                          accountEmail: item.email,
                          accountService: item.service,
                        }));
                      }
                    }}
                  />
                  <Divider sx={{margin: '12px 0px'}} />
                </React.Fragment>
              ))
            }
          </Box>
        </Box>
        <AvailabilityPreference
          open={showAvailabilityPreference}
          onClose={() => {
            setShowAvailabilityPreference(false);
            setShowMenu(false);
          }}
          account={activeItemOnMenu}
        />
        <ConnectAccountModal
          open={showConnectAccountModal}
          onClose={() => setShowConnectAccountModal(false)}
          onSuccess={(account) => {
            setRecentlyAddedAccount(account);
            if (bindAccount && !isAccountMatch(bindAccount, {email: account.mail, service: account.service})) {
              setShowMakeAccountDefaultConfirm(true);
            }
            setShowConnectAccountModal(false);
            setShowMenu(false);
          }}
        />

        <SyncPreference
          account={activeItemOnMenu}
          accountList={list}
          open={isCancellationsDialogOpen}
          onSubmitted={() => {
            setIsCancellationsDialogOpen(false);
          }}
          onClose={() => setIsCancellationsDialogOpen(false)}
        />

        {recentlyAddedAccount.mail && (
          <ZmDialog
            maxWidth="xs"
            open={showMakeAccountDefaultConfirm}
          >
            <DialogTitle>{t('integration.defaultAccountSettings',
              {service: serviceNameMap[recentlyAddedAccount.service] || ''})}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" gutterBottom>{t('integration.makeAccountDefault',
                {service: serviceNameMap[recentlyAddedAccount.service] || ''})}
              </Typography>
            </DialogContent>
            <DialogActions>
              <ZmPrimaryBtn
                size="small"
                onClick={async () => {
                  setShowMakeAccountDefaultConfirm(false);
                  const account = list.find((acc) =>
                    isAccountMatch(acc, {email: recentlyAddedAccount.mail, service: recentlyAddedAccount.service})
                  );
                  if (account) {
                    // Open the sync account settings for the new primary account
                    setActiveItemOnMenu(account);
                    setIsCancellationsDialogOpen(true);
                  }
                }}
              >
                {t('common.yesSwitch', {service: serviceNameMap[recentlyAddedAccount.service] || ''})}
              </ZmPrimaryBtn>
              <ZmTertiaryBtn
                size="small"
                onClick={() => setShowMakeAccountDefaultConfirm(false)}
              >
                {t('common.keepCurrent')}
              </ZmTertiaryBtn>
            </DialogActions>
          </ZmDialog>
        )}

        <ZmDialog open={showDisconnectConfirm}>
          <DialogTitle>{t('integration.disconnect')} {activeItemOnMenu?.email}?</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              {t('integration.disconnectConsequence')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              disabled={loadingState.isLoading}
              sx={{fontSize: '14px', height: '32px'}}
              color="error"
              onClick={async () => {
                setShowMenu(false);
                const {meta} = await dispatch(
                  disconnectAccount(activeItemOnMenu)
                );
                if (meta.requestStatus === 'fulfilled') {
                  setShowDisconnectConfirm(false);
                }
              }}
            >
              {t('integration.disconnect')}
            </Button>
            <ZmTertiaryBtn
              disabled={loadingState.isLoading}
              onClick={() => setShowDisconnectConfirm(false)}
              sx={{fontSize: '14px', height: '32px'}}
            >
              {t('common.cancel')}
            </ZmTertiaryBtn>
          </DialogActions>
        </ZmDialog>
      </>
      }
    </Box>
  </>;
}
